<template>
    <div v-if="!loading">
        <DecisionModal
            :is-open="isOpen"
            size="fluid"
            data-qa="decision-diamond-modal"
            class="decision-diamond-modal"
            :title="$t('funnel.edit.decision.diamond')"
            :cell="cell"
            :show-debug-data-dump="showStealthMenu"
            @close-modal="closeModal"
            @save-decision-diamond="saveAndCloseModal"
            @update-config="saveCellValue"
            @open-dd-test-modal="showTestDecisionDiamondModal"
            @open-dd-debug-modal="showDebugDecisionDiamondModal"
        >
            <template #diamondParameters>
                <section class="vue-right-controls">
                    <DecisionDiamondTest
                        v-if="showDecisionDiamondTest"
                        data-qa="decision-diamond-test"
                        :is-open="showDecisionDiamondTest"
                        :source-id="decisions.sourceId"
                        :funnel-id="decisions.funnelId"
                        :flow-id-title="decisions.flowIdSequenceTitle"
                        :decision-ids="decisions.allDecisionIds"
                        :dd-keys="ddKeys"
                        @close-dd-test-modal="closeDecisionDiamondTest"
                    />

                    <DecisionDiamondDebug
                        v-if="showDecisionDiamondDebug"
                        data-qa="decision-diamond-debug"
                        :is-open="showDecisionDiamondDebug"
                        :decision-ids="decisions.allDecisionIds"
                        @close-dd-debug-modal="closeDecisionDiamondDebug"
                    />
                </section>

                <div class="decision-container">
                    <div
                        v-for="key in sortedRuleGroupKeys"
                        :key="key"
                    >
                        <DecisionDiamondRuleGroup
                            :decisions="decisions"
                            :rule-group="decisions.ruleGroups[key]"
                            :dd-keys="ddKeys"
                            @local-rule-groups-update="handleRuleGroupUpdate($event, key)"
                        />
                    </div>
                    <div class="default-multiselect">
                        <div v-if="defaultOptionsVisible">
                            <div class="default-label-multiselect">
                                {{ $t('funnel.edit.decision.default.label') }}
                            </div>
                            <DsMultiselect
                                v-model="selectedDefault"
                                data-qa="default-multiselect-option"
                                :options="defaultOptions"
                                :placeholder="$t('funnel.edit.decision.select.default')"
                                @input="saveDefaultOption"
                            />
                        </div>
                    </div>
                </div>
            </template>
        </DecisionModal>
    </div>
</template>
<script>
import {
    getDecisionConfiguration,
    sequenceSaveDefaultOption,
} from './decision.api';
import DecisionModal from './DecisionModal.vue';
import DecisionDiamondTest from './DecisionDiamondTest.vue';
import DecisionDiamondDebug from './DecisionDiamondDebug.vue';
import DecisionDiamondRuleGroup from './DecisionDiamondRuleGroup.vue';
import {
    findIncomingEdges,
    findOutgoingCells,
    findOutgoingEdges,
    findIncomingCells,
} from '../utils/cell/automation-edge-utils';
import { createDecisionsAsNecessary, isDecisionDiamondReady } from './decision-utils';
import {
    GOAL_INTERNAL_FORM,
    GOAL_SMART_FORM,
    GOAL_WEB_FORM,
    INTEGRATION_TRIGGER_TYPE,
} from '../utils/editor.constants';
import { utils } from '../utils/legacy-utils';

export const DECISION_FORMS = {
    SMART_FORM: 'SmartForm',
    WEB_FORM: 'WebForm',
};

export default {
    DECISION_FORMS,
    components: {
        DecisionModal,
        DecisionDiamondRuleGroup,
        DecisionDiamondTest,
        DecisionDiamondDebug,
    },
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        cell: {
            type: Object,
            default: () => ({
                value: {
                    decisions: [],
                },
            }),
        },
        cells: {
            type: Array,
            default: () => ([]),
        },
        funnelId: {
            type: Number,
            required: true,
        },
        appInfo: {
            type: Object,
            required: true,
        },
    },
    emits: ['close-modal', 'update-cell-value'],
    data() {
        return {
            selectedDefault: null,
            defaultOptions: [],
            ddKeys: {
                primaryKey: 'Marketing',
                secondaryKey: '',
                secondaryKeyId: 0,
            },
            ruleGroupsState: {},
            showDecisionDiamondTest: false,
            showDecisionDiamondDebug: false,
            decisions: {
                sourceId: 0,
                funnelId: 0,
                secondaryKey: '',
                defaultDecisionId: 0,
                allDecisionIds: [],
                secondaryKeyId: 0,
                flowNames: [],
                flowIdSequenceTitle: {},
                decisionNames: [],
                flowIds: [],
                decisionIds: [],
            },
            loading: true,
        };
    },
    computed: {
        showStealthMenu() {
            return this.appInfo?.appEditionFlags?.local;
        },
        cellId() {
            return this.cell?.id;
        },
        sortedRuleGroupKeys() {
            if (!this.decisions || !this.decisions.ruleGroups) {
                return [];
            }

            const result = Object.entries(this.decisions?.ruleGroups)
                .sort(([, rGroup1], [, rGroup2]) => rGroup1.edgeSourceId - rGroup2.edgeSourceId)
                .map(([key]) => key);

            return result;
        },
        defaultOptionsVisible() {
            if (Object.keys(this.ruleGroupsState).length > 0) {
                return Object.values(this.ruleGroupsState).every((hasRules) => hasRules) || false;
            }

            return false;
        },
        uniqueDecisionNames() {
            const unique = {};

            return this.decisions.decisionNames.filter((decisionName) => {
                if (!unique[decisionName.value]) {
                    unique[decisionName.value] = true;

                    return true;
                }

                return false;
            });
        },
    },
    async mounted() {
        try {
            this.parseIncomingKeys();
            await this.configKonvaDecision();
            this.setDefaultOptions();
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error loading data:', error);
        } finally {
            this.loading = false;
        }
    },
    methods: {
        handleRuleGroupUpdate(eventData, key) {
            this.updateRuleGroups(eventData, key);
        },
        updateRuleGroups(eventData, key) {
            const { ruleGroupId, hasRules, localRuleGroups } = eventData;

            this.handleLocalRuleGroupsUpdate({
                ruleGroupId,
                hasRules,
                localRuleGroups,
                sequenceId: key,
            });
        },
        closeDecisionDiamondTest() {
            this.showDecisionDiamondTest = false;
        },
        closeDecisionDiamondDebug() {
            this.showDecisionDiamondDebug = false;
        },
        parseIncomingKeys() {
            const [firstIncomingCell] = findIncomingCells(this.cellId, this.cells);
            const { meta: { metaType }, value } = firstIncomingCell;

            this.setDdKeys(metaType, value);
        },
        setDdKeys(metaType, value) {
            const isGoalForm = [GOAL_INTERNAL_FORM, GOAL_WEB_FORM].includes(metaType);
            const isGoalSmartForm = GOAL_SMART_FORM === metaType;
            const isIntegration = (value.type === INTEGRATION_TRIGGER_TYPE);

            let secondaryKey; let secondaryKeyId;

            if (isIntegration) {
                secondaryKey = INTEGRATION_TRIGGER_TYPE;
                secondaryKeyId = this.funnelId;
            } else if (isGoalSmartForm) {
                secondaryKey = this.$options.DECISION_FORMS.SMART_FORM;
                secondaryKeyId = value.smartFormInstanceId;
            } else {
                secondaryKey = isGoalForm ? this.$options.DECISION_FORMS.WEB_FORM : '';
                secondaryKeyId = isGoalForm ? value.internalFormId || value.webformId : 0;
            }

            this.ddKeys = {
                ...this.ddKeys,
                secondaryKey,
                secondaryKeyId: utils.removeLongSuffix(secondaryKeyId),
            };
        },
        async configKonvaDecision() {
            if (this.cell?.value?.decisions?.flowIds?.length > 0) {
                const { value: { decisions } } = this.cell;
                const flowIds = [];
                const decisionIds = [];

                decisions.forEach(({ flowId, decisionId }) => {
                    flowIds.push(flowId);
                    decisionIds.push(decisionId);
                });
                await this.configureDecisionMappings(flowIds, decisionIds);
            } else {
                const { flowIds, decisionIds } = await createDecisionsAsNecessary(this.cellId, this.cells);

                await this.configureDecisionMappings(flowIds, decisionIds);
            }
        },
        async configureDecisionMappings(flowIds, decisionIds) {
            this.decisions.flowIds = flowIds;
            this.decisions.decisionIds = decisionIds;
            const resultIncomingEdges = findIncomingEdges(this.cellId, this.cells);
            const { meta: { source } } = resultIncomingEdges[0];
            const resultOutgoingCells = findOutgoingCells(this.cellId, this.cells);
            const outgoingEdges = findOutgoingEdges(this.cellId, this.cells);

            const edgesIdFromDecision = outgoingEdges.map((out) => ({
                edgeId: out.id,
                flowId: out.meta.target,
            }));

            const filterOutgoingKeys = resultOutgoingCells.map((out) => out.id);

            const decisionConfiguration = await getDecisionConfiguration({
                flowIds,
                decisionIds,
                ...this.ddKeys,
            });

            const ruleGroupsTemp = decisionConfiguration.ruleGroups;

            const filteredRuleGroups = Object.keys(ruleGroupsTemp)
                .filter((key) => filterOutgoingKeys.includes(key))
                .reduce((acc, key) => {
                    acc[key] = ruleGroupsTemp[key];

                    return acc;
                }, {});

            const idTitleMap = new Map();

            resultOutgoingCells.forEach(({ id, value }) => {
                idTitleMap.set(id, value.name);

                if (filteredRuleGroups[id]) {
                    filteredRuleGroups[id] = {
                        ...filteredRuleGroups[id],
                        title: value.name,
                        edgeSourceId: edgesIdFromDecision.map((edgeFlow) => {
                            if (edgeFlow.flowId === id) {
                                return parseInt(edgeFlow.edgeId, 10);
                            }

                            return null;
                        }).filter((edgeId) => edgeId !== null)[0],
                    };
                }
            });

            const decisionNames = [];

            [...idTitleMap.entries()].forEach(([key, value]) => {
                const keyInt = parseInt(key, 10);

                if (decisionConfiguration.flowDecision[keyInt]) {
                    decisionNames.push({
                        value: decisionConfiguration.flowDecision[keyInt],
                        label: value,
                    });
                }
            });

            this.decisions = {
                sourceId: parseInt(source, 10),
                funnelId: this.funnelId,
                flowIdSequenceTitle: Object.fromEntries(idTitleMap),
                flowNames: flowIds.map((flowId) => idTitleMap.get(`${flowId}`)),
                allDecisionIds: decisionIds,
                ruleGroups: filteredRuleGroups,
                flowDecision: decisionConfiguration.flowDecision,
                defaultDecisionId: decisionConfiguration.defaultDecisionId,
                decisionNames,
                decisionIds,
                flowIds,
            };

            Object.values(this.decisions.ruleGroups).forEach((ruleGroup) => {
                let ruleGroupId = 0;
                let hasRules = false;

                if (ruleGroup || ruleGroup.ruleGroupMap || Object.keys(ruleGroup.ruleGroupMap).length > 0) {
                    ruleGroupId = ruleGroup.id;
                    Object.values(ruleGroup.ruleGroupMap).forEach((ruleGroupMap) => {
                        if (ruleGroupMap || ruleGroupMap.rulesMap || Object.keys(ruleGroupMap.rulesMap).length > 0) {
                            hasRules = true;
                        }
                    });
                }
                this.handleLocalRuleGroupsUpdate({ ruleGroupId, hasRules });
            });

            this.saveCellValue();
        },
        setDefaultOptions() {
            const defaultValueLabelPair = { value: 0, label: this.$t('funnel.edit.decision.select.option') };
            const filteredOptions = this.uniqueDecisionNames.filter((option) => option.value !== defaultValueLabelPair.value);

            this.defaultOptions = [defaultValueLabelPair, ...filteredOptions];
            this.selectedDefault = this.defaultOptions.find((option) => option.value === this.decisions?.defaultDecisionId) || defaultValueLabelPair;
        },
        handleLocalRuleGroupsUpdate({
            ruleGroupId, hasRules, localRuleGroups, sequenceId,
        }) {
            this.$set(this.ruleGroupsState, ruleGroupId, hasRules);

            if (localRuleGroups) {
                const existingRuleGroupsInDecisions = Object.keys(this.decisions.ruleGroups[sequenceId].ruleGroupMap);
                const localRuleGroupIds = new Set(localRuleGroups.map((group) => group.id));

                localRuleGroups.forEach((ruleGroupMap) => {
                    if (!existingRuleGroupsInDecisions.includes(ruleGroupMap.id)) {
                        this.decisions.ruleGroups[sequenceId].ruleGroupMap[ruleGroupMap.id] = ruleGroupMap;
                    }
                });

                existingRuleGroupsInDecisions.forEach((id) => {
                    if (!localRuleGroupIds.has(Number(id))) {
                        delete this.decisions.ruleGroups[sequenceId].ruleGroupMap[id];
                    }
                });
            }

            this.saveCellValue();
        },
        closeModal() {
            this.$emit('close-modal');
        },

        saveAndCloseModal() {
            this.saveCellValue();
            this.closeModal();
        },

        saveCellValue() {
            let decisionItems = [];
            const { flowIds, decisionIds } = this.decisions;

            decisionItems = flowIds.filter((element) => !!element).map((flowId, index) => {
                return {
                    flowId,
                    decisionId: decisionIds[index],
                };
            });

            const cellValue = { ...this.cell.value, decisions: decisionItems };
            const cell = { ...this.cell, value: cellValue };

            const isReady = isDecisionDiamondReady(this.cell.id, this.cells);

            this.$emit('update-cell-value', { cell, preventClose: true, isReady });
        },
        async saveDefaultOption() {
            const payload = {
                decisionId: this.selectedDefault.value,
                decisionList: this.defaultOptions.filter((d) => d.value !== 0).map((d) => d.value),
            };

            await sequenceSaveDefaultOption(payload);
        },
        showTestDecisionDiamondModal() {
            this.showDecisionDiamondTest = true;
        },
        showDebugDecisionDiamondModal() {
            this.showDecisionDiamondDebug = true;
        },
    },
};
</script>
<style lang="scss" scoped>
    .default-multiselect {
        width: px-to-rem(375);
        margin: $spacing-600 auto 0;
    }

    .default-label-multiselect {
        margin-bottom: $spacing-250;
        text-align: center;
    }
</style>
